import { useState } from "react";
import { toast } from "sonner";
import { CalendarClock, Loader2 } from "lucide-react";

import { Button } from "../../components/ui/button";
import { useDiaVencimento } from "./useDiaVencimento";

const MESES = ["janeiro", "fevereiro", "março", "abril", "maio", "junho", "julho",
  "agosto", "setembro", "outubro", "novembro", "dezembro"];
// "2026-11" -> "novembro de 2026"
const mesPorExtenso = (chave) => {
  if (!chave) return "";
  const [ano, mes] = chave.split("-");
  return `${MESES[Number(mes) - 1]} de ${ano}`;
};
const dataBR = (iso) => (iso ? iso.split("-").reverse().join("/") : "");

/** Cartão "Dia de vencimento" da tela de Assinatura. O lojista escolhe entre 5,
 *  10, 15, 20 e 25; a troca só vale do mês seguinte em diante (o servidor é quem
 *  decide o mês — aqui só se mostra). */
export default function DiaVencimento({ podeAlterar }) {
  const { dados, salvando, trocar } = useDiaVencimento();
  const [escolhido, setEscolhido] = useState(null);

  if (!dados) return null;
  const { dia_atual: diaAtual, opcoes = [], troca, vale_desde: valeDesde, fim_teste: fimTeste } = dados;

  const escolher = (dia) => {
    if (!podeAlterar) {
      toast.error("Apenas o proprietário ou administrador pode trocar o dia de vencimento");
      return;
    }
    if (dia === diaAtual && !troca) return;      // já é o dia, nada a fazer
    setEscolhido(dia === escolhido ? null : dia);
  };

  const confirmar = async () => {
    const cancelando = escolhido === diaAtual;
    if (await trocar(escolhido)) {
      toast.success(cancelando ? "Troca cancelada" : `Dia ${escolhido} a partir de ${mesPorExtenso(valeDesde)}`);
      setEscolhido(null);
    }
  };

  return (
    <div className="bg-card border rounded-2xl px-5 py-4" data-testid="dia-vencimento">
      <div className="flex items-start gap-4 flex-wrap">
        <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center text-primary shrink-0">
          <CalendarClock className="w-5 h-5" />
        </div>
        <div className="min-w-0 flex-1">
          <p className="font-bold leading-tight">Dia de vencimento</p>
          <p className="text-xs text-muted-foreground mt-0.5">
            Sua mensalidade vence todo <b className="text-foreground">dia {diaAtual}</b>.
            {fimTeste && ` Seu teste vai até ${dataBR(fimTeste)}: a 1ª mensalidade vence no dia útil seguinte.`}
          </p>
          {troca && (
            <p className="text-xs font-semibold text-amber-600 dark:text-amber-400 mt-1">
              Troca agendada: a partir de {mesPorExtenso(troca.desde)}, todo dia {troca.dia}.{" "}
              {podeAlterar && (
                <button type="button" onClick={() => setEscolhido(diaAtual)} disabled={salvando}
                  className="underline font-bold hover:opacity-80">
                  Cancelar troca
                </button>
              )}
            </p>
          )}
        </div>
        <div className="flex gap-1.5 flex-wrap">
          {opcoes.map((dia) => {
            const atual = dia === diaAtual;
            const agendado = troca?.dia === dia;
            const marcado = escolhido === dia;
            return (
              <button key={dia} type="button" onClick={() => escolher(dia)} disabled={salvando}
                aria-pressed={atual}
                className={`w-12 h-12 rounded-xl border-2 text-sm font-bold tabular-nums transition-colors ${
                  atual ? "border-primary bg-primary/10 text-primary"
                    : marcado || agendado ? "border-amber-500 bg-amber-500/10 text-amber-600 dark:text-amber-400"
                      : "border-border text-foreground hover:border-primary/50"}`}>
                {dia}
              </button>
            );
          })}
        </div>
      </div>

      {escolhido !== null && (
        <div className="mt-3 pt-3 border-t flex items-center gap-3 flex-wrap">
          <p className="text-sm flex-1 min-w-[220px]">
            {escolhido === diaAtual
              ? <>Cancelar a troca e continuar no <b>dia {diaAtual}</b>?</>
              : <>Trocar para o <b>dia {escolhido}</b>? A fatura que já existe não muda de data — a troca vale a partir de <b>{mesPorExtenso(valeDesde)}</b>.</>}
          </p>
          <Button variant="outline" size="sm" onClick={() => setEscolhido(null)} disabled={salvando}>Voltar</Button>
          <Button size="sm" onClick={confirmar} disabled={salvando}>
            {salvando && <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />}
            Confirmar
          </Button>
        </div>
      )}

      <p className="text-[11px] text-muted-foreground mt-2">
        A troca vale sempre a partir do mês seguinte. A fatura fica disponível 10 dias antes do vencimento.
      </p>
    </div>
  );
}
